<template lang="pug">
#meeting-room.d-flex.flex-column
  .title {{ title }}

  .main-content.w-100.d-flex.flex-column.flex-grow-1
    //- 入室前：プレビュー
    .preview-container.p-4.d-flex.flex-column.align-items-center(v-if="!isJoined && !errorMessage")
      .video-preview
        //- 自分の顔を映すプレビュー用
        video.preview-video(ref="previewVideo", autoplay, muted, playsinline)

      .device-selectors.d-flex.flex-column.justify-content-start
        span カメラ
        b-form-select(v-model="selectedVideoId" @change="onChangeVideoDevice")
          option(v-for="device in videoDevices" :key="device.deviceId" :value="device.deviceId") {{ device.label }}
        .mb-2
        span マイク
        b-form-select(v-model="selectedAudioId" @change="onChangeAudioDevice")
          option(v-for="device in audioDevices" :key="device.deviceId" :value="device.deviceId") {{ device.label }}
        .audio-indicator
          p マイク入力レベル（声を出すと動きます）
          .meter
            .meter-bar(:style="{ width: micVolume + '%' }")
        .mb-4
        .d-flex.flex-column.justify-content-start.align-items-center
          button.btn.meeting-start-btn(@click="joinMeeting(roomId, type)") 通話開始

    //- 入室後：会議画面
    .meeting-container.w-100.d-flex.flex-column.flex-grow-1(v-else-if="isJoined && !errorMessage")
      .video-wrapper.flex-grow-1.w-100
        //- 自分のプレビュー用（ワイプ表示）。v-showで表示切替
        .local-video-wipe(v-show="isLocalVideoOn")
          video(ref="localVideo", autoplay, muted, playsinline, style="transform: scaleX(-1);")
        
        //- 他の参加者用グリッド
        .video-grid(:style="gridStyle")
          //- 左上の余白（ワイプの下敷き）用ダミー要素
          .video-item.dummy(
            v-for="n in emptyCellCount"
            :key="'dummy-' + n"
          )
          .video-item(
            v-for="tileId in remoteTileIds"
            :key="tileId"
            @click="togglePin(tileId)"
            class="clickable"
            :style="getItemStyle(tileId)"
            :title="pinnedTileId === tileId ? 'クリックして元のサイズに戻す' : 'クリックして拡大する'"
          )
            video(:ref="'remoteVideo-' + tileId", autoplay, playsinline)

      //- 音声再生用の隠し要素
      audio(ref="audioElement", style="display:none")

      .controls.d-flex.justify-content-center.align-items-center.p-3
        button.btn.mr-2(@click="toggleMicMute" title="マイクミュート オン／オフ")
          b-icon(:icon="isMicMuted ? 'mic-mute' : 'mic'" style="font-size: 150%;")
        button.btn.mr-2(@click="toggleSpeakerMute" title="スピーカーミュート オン／オフ")
          b-icon(:icon="isSpeakerMuted ? 'volume-mute' : 'volume-up'" style="font-size: 150%;")
        button.btn.mr-2(@click="toggleLocalVideo" title="自分のビデオ表示 オン／オフ")
          b-icon(:icon="isLocalVideoOn ? 'camera-video' : 'camera-video-off'" style="font-size: 150%;")
        button.btn.mr-2(v-if="showScreenShot" @click="onScreenShot" title="スクリーンショット")
          b-icon(:icon="'camera'" style="font-size: 150%;")
        button.btn.leave-meeting-btn(@click="leaveMeeting") 退出

    //- エラー
    .error-message-container.p-4.d-flex.flex-column.align-items-center(v-else-if="errorMessage")
      .error-message {{ errorMessage }}

  //- バージョン表示
  .version.p-1
    span ver.{{ appVersion }}

  //- メッセージモーダル
  OkModal(v-model="showMessageModal" :title="messageModalTitle" :message="messageModalMessage")
  //- ページローディング
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue, Ref } from "vue-property-decorator";
import { ConsoleLogger, DefaultDeviceController, DefaultMeetingSession, LogLevel, MeetingSessionConfiguration, AudioVideoObserver, VideoTileState } from "amazon-chime-sdk-js";

import MeetingService, { MeetingSessionType, StartMeetingGetModel, UploadImagePostModel } from "@/commons/services/Meeting";
import SendSystemErrorService from "@/commons/services/SendSystemError";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import SystemError from "../models/SystemError";

/**
 * ミーティング画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkModal
  }
})
export default class MeetingRoom extends Vue {
  /** ミーティングサービス */
  @Inject("MeetingService") meetingService!: MeetingService;
  /** システムエラーサービス */
  @Inject("SendSystemErrorService") sendSystemErrorService!: SendSystemErrorService;

  /** プレビュービデオエリア */
  @Ref("previewVideo") readonly previewVideoElement!: HTMLVideoElement;

  /** 会議用ビデオエリア */
  @Ref("localVideo") readonly localVideoElement!: HTMLVideoElement;
  /** オーディオ要素 */
  @Ref("audioElement") readonly audioElement!: HTMLAudioElement;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** メッセージモーダル表示可否 */
  showMessageModal: boolean = false;
  /** メッセージモーダルタイトル */
  messageModalTitle: string = "";
  /** メッセージモーダル本文 */
  messageModalMessage: string = "";

  /** 部屋ID */
  roomId: string | null = null;
  /** 部屋種別 */
  type: string | null = null;
  /** スクリーンショット使用可否 */
  showScreenShot: boolean = false;
  /** 施設ID */
  facilityId: number | null = null;
  /** 面会ID */
  reservationId: number | null = null;

  /** ページタイトル */
  title: string = "";

  /** 入室状態 */
  isJoined: boolean = false;

  /** chimeSDK デバイスコントーラー */
  deviceController: DefaultDeviceController | null = null;
  /** ビデオ入力デバイス一覧（プレビュー用） */
  videoDevices: MediaDeviceInfo[] = [];
  /** オーディオ入力デバイス一覧（プレビュー用） */
  audioDevices: MediaDeviceInfo[] = [];
  /** 選択されたビデオデバイスID */
  selectedVideoId: string = "";
  /** 選択されたオーディオデバイスID */
  selectedAudioId: string = "";
  /** マイク音量 */
  micVolume: number = 0; // マイク音量の可視化用
  /** アニメーションフレームID */
  animationFrameId: number = 0;
  /** Chime SDK の AnalyserNode */
  analyserNode: any = null;
  /** Chime SDK オブザーバー */
  observer: AudioVideoObserver | null = null;

  /** ミーティング情報情報 */
  meeting: StartMeetingGetModel | null = null;
  /** ミーティングセッション情報 */
  meetingSession: DefaultMeetingSession | null = null;

  /** 他参加者の映像タイルIDのリスト */
  remoteTileIds: number[] = [];

  /** ピン留めされたビデオタイルID */
  pinnedTileId: number | null = null;

  /** エラーメッセージ */
  errorMessage: string = "";

  /** アプリケーションバージョン */
  appVersion: string = process.env.VUE_APP_VERSION || "";

  /** マイクのミュート状態 */
  isMicMuted: boolean = false;
  /** スピーカーのミュート状態 */
  isSpeakerMuted: boolean = false;
  /** ローカルビデオ表示状態 */
  isLocalVideoOn: boolean = true;

  /** ウィンドウ幅（画面アスペクト比計算用） */
  windowWidth: number = window.innerWidth;
  /** ウィンドウ高さ（画面アスペクト比計算用） */
  windowHeight: number = window.innerHeight;

  /**
   * ### vue lifecycle mounted
   */

  async mounted(): Promise<void> {
    // 通話中のビデオサイズを計算するために、ウィンドウのリサイズを検知できるようにする
    window.addEventListener("resize", this.handleResize);

    // クエリパラメータから画面情報を初期化する
    await this.initialize();

    // プレビュー用情報を作成する
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const logger = new ConsoleLogger("PreviewLogger", LogLevel.INFO);
    this.deviceController = new DefaultDeviceController(logger);

    // デバイス一覧の取得
    this.videoDevices = await this.deviceController.listVideoInputDevices();
    this.audioDevices = await this.deviceController.listAudioInputDevices();

    // ビデオ、音声デバイスが見つかった場合、初期値をセットする
    if (this.audioDevices.length > 0) {
      this.selectedAudioId = this.audioDevices[0].deviceId;
    }
    if (this.videoDevices.length > 0) {
      this.selectedVideoId = this.videoDevices[0].deviceId;
      await this.startPreview();
    }

    this.isBusy = false;

    // 画面説明モーダルを表示する
    this.showFeatureGuide();

    return;
  }

  /**
   * ### 初期化処理
   *
   * - クエリパラメータからミーティングに必要な情報を取得する
   * - 必要な情報から画面タイトル等を作成する
   */
  private async initialize(): Promise<void> {
    // ミーティング情報を取得するのに必要な情報を取得する
    const roomId: string | null = this.$route.query.room_id as string | null;
    const type: string | null = this.$route.query.type as string | null;

    // どちらかが値が存在しない場合はエラーを表示する
    if (!roomId || !type) {
      // エラーはerrorCollectionsに登録
      await this.sendSystemErrorService.postForPublic(
        { message: `roomId or type is missing. roomId: ${roomId}, type: ${type}` },
        this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
        this.$router
      );

      // モーダルと画面表示でエラーを通知する
      this.messageModalTitle = "エラーが発生しました。";
      const messages: string[] = [];
      messages.push("URLに誤りがあるため、通話を開始できません。");
      messages.push("再度URLを確認していただき、もう一度お試しください。");
      this.messageModalMessage = messages.join("\n");
      this.showMessageModal = true;

      this.errorMessage = messages.join("\n");

      return;
    }
    this.roomId = roomId;
    this.type = type;

    // スクリーンショット使用可否に関わる情報を取得する（LINE LIFFからリダイレクトされた時に格納されている）
    const photo: boolean = this.$route.query.photo == "on" ? true : false;
    const facilityId: string | null = this.$route.query.facility_id as string | null;
    const reservationId: string | null = this.$route.query.reservation_id as string | null;

    this.facilityId = facilityId && !isNaN(Number(facilityId)) ? Number(facilityId) : null;
    this.reservationId = reservationId && !isNaN(Number(reservationId)) ? Number(reservationId) : null;
    this.showScreenShot = photo == true && this.facilityId && this.reservationId && this.canScreenShotOfType(type) ? true : false;

    // 画面タイトルを変更
    document.title = `HitomeQコネクト ${this.convertMeetingSessionTypeName(type)}`;
    this.title = `HitomeQコネクト ${this.convertMeetingSessionTypeName(type)}`;

    // デバッグ用
    // alert(`photo:${photo}\nfacilityId:${facilityId}\nreservationId:${reservationId}`);

    return;
  }

  /** ### スクリーンショット可能な種別なのかを調べて返す */
  private canScreenShotOfType(type: string): boolean {
    switch (type) {
      case "reservation":
        return true;
      default:
        return false;
    }
  }

  /**
   * ### プレビュー開始処理
   */
  private async startPreview() {
    if (!this.deviceController || !this.selectedVideoId || !this.previewVideoElement) return;

    // カメラを起動して video 要素にバインド
    await this.deviceController.startVideoInput(this.selectedVideoId);
    this.deviceController.startVideoPreviewForVideoInput(this.previewVideoElement);

    // マイクの起動（音量メーター用）
    if (this.selectedAudioId) {
      await this.deviceController.startAudioInput(this.selectedAudioId);
      this.setupAudioMeter();
    }

    return;
  }

  /**
   * ### マイクの出力音量バーをセットアップする
   */
  private setupAudioMeter() {
    if (!this.deviceController) return;
    this.stopAudioMeter(); // 既存のメーター処理があれば停止

    try {
      // Chime SDK の機能を使用して AnalyserNode を作成
      this.analyserNode = this.deviceController.createAnalyserNodeForAudioInput();
      if (!this.analyserNode) return;

      const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

      const updateMeter = () => {
        if (!this.analyserNode) return;

        this.analyserNode.getByteFrequencyData(dataArray);
        let sum = 0;
        // 音声データの平均値を計算
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const average = sum / dataArray.length;

        // 0〜255の値を0〜100%に変換 (少し感度を上げるために1.5倍)
        this.micVolume = Math.min(100, Math.floor((average / 255) * 100 * 1.5));

        // 次の描画フレームで再度実行
        this.animationFrameId = requestAnimationFrame(updateMeter);
      };

      updateMeter();
    } catch (e) {
      console.warn("Audio meter setup failed:", e);
    }

    return;
  }

  /**
   * ### マイクの出力音量バーを停止する
   */
  private stopAudioMeter() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = 0;
    }
    if (this.analyserNode) {
      if (typeof this.analyserNode.removeOriginalInputs === "function") {
        this.analyserNode.removeOriginalInputs();
      } else if (typeof this.analyserNode.disconnect === "function") {
        this.analyserNode.disconnect();
      }
      this.analyserNode = null;
    }
    this.micVolume = 0;
  }

  /**
   * ### ビデオデバイスが変更された時の処理
   */
  async onChangeVideoDevice() {
    await this.startPreview();
  }

  /**
   * ### オーディオデバイスが変更された時の処理
   */
  async onChangeAudioDevice(): Promise<void> {
    // IDに値が無い場合は処理しない
    if (!this.selectedAudioId) {
      console.warn("No audio device selected");
      return;
    }

    try {
      if (this.deviceController) {
        // 指定されたデバイスIDで入力を開始
        await this.deviceController.startAudioInput(this.selectedAudioId);
        this.setupAudioMeter();
      }
    } catch (error) {
      // ここでエラーをキャッチすれば、Loggerへの出力も制御できます
      console.error("Failed to start audio input:", error);
    }
  }

  /**
   * ### 画面初期表示の説明モーダルを表示する
   */
  private showFeatureGuide(): void {
    this.messageModalTitle = "";
    const messages: string[] = [];
    messages.push("通話開始前にカメラとマイクの動作を確認できます。");
    messages.push("使用できない場合は、カメラとマイクの設定（使用許可）をご確認ください。");
    messages.push("");
    messages.push("通話開始を選択すると通話が始まります。");
    messages.push("開始しても参加者が入っていない場合は、参加者が入るまでお待ちください。");
    messages.push("通話中に参加者のビデオをタップすると拡大し、再度タップすると元に戻ります。");
    this.messageModalMessage = messages.join("\n");
    this.showMessageModal = true;

    return;
  }

  /**
   * ### ミーティング参加処理
   */
  async joinMeeting(roomId: string, type: string): Promise<void> {
    try {
      this.isBusy = true;
      this.loadingMessage = "接続中...";

      // プレビューを一度止める
      if (this.deviceController) {
        this.deviceController.stopVideoPreviewForVideoInput(this.previewVideoElement);
      }
      this.stopAudioMeter();

      // ミーティング情報を取得する
      const meetingSessionType = this.convertMeetingSessionType(type);
      if (!meetingSessionType) {
        // エラーはerrorCollectionsに登録
        await this.sendSystemErrorService.postForPublic(
          { message: `joinMeeting() error. meetingSessionType is invalid. roomId: ${roomId}, type: ${type}` },
          this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
          this.$router
        );

        // モーダルと画面表示でエラーを通知する
        this.messageModalTitle = "エラーが発生しました。";
        const messages: string[] = [];
        messages.push("URLに誤りがあるため、通話を開始できません。");
        messages.push("再度URLを確認していただき、もう一度お試しください。");
        this.messageModalMessage = messages.join("\n");
        this.showMessageModal = true;

        this.errorMessage = messages.join("\n");
        return;
      }
      const meeting: StartMeetingGetModel = await this.meetingService.get(roomId, meetingSessionType);
      this.meeting = meeting;

      // Chime Sessionの初期設定
      const logger = new ConsoleLogger("ChimeLogger", LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger);
      const configuration = new MeetingSessionConfiguration(meeting.meetingData, meeting.attendee);
      this.meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

      // Observer（監視者）の定義
      this.observer = {
        videoTileDidUpdate: (tileState: VideoTileState) => {
          if (!tileState.tileId || !this.meetingSession) return;

          if (tileState.localTile) {
            // 自分のビデオをバインド
            if (this.localVideoElement) {
              this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, this.localVideoElement);
            }
          } else {
            // 他人のビデオタイルが追加された場合
            if (!this.remoteTileIds.includes(tileState.tileId)) {
              this.remoteTileIds.push(tileState.tileId);

              // DOMが更新されるのを待ってからバインド
              this.$nextTick(() => {
                const el = this.$refs[`remoteVideo-${tileState.tileId}`] as HTMLVideoElement | HTMLVideoElement[];
                const videoEl = Array.isArray(el) ? el[0] : el;
                if (videoEl) {
                  this.meetingSession!.audioVideo.bindVideoElement(tileState.tileId!, videoEl);
                }
              });
            }
          }
        },
        videoTileWasRemoved: (tileId: number) => {
          this.remoteTileIds = this.remoteTileIds.filter((id) => id !== tileId);
          // ピン留めしていたユーザーが退出した場合はピン留めを解除する
          if (this.pinnedTileId === tileId) {
            this.pinnedTileId = null;
          }
        }
      };

      this.meetingSession.audioVideo.addObserver(this.observer);

      // デバイスの起動と通話開始
      // プレビューで選択されたカメラがあればそれを使用する
      if (this.selectedVideoId) {
        await this.meetingSession.audioVideo.startVideoInput(this.selectedVideoId);
      } else {
        const videoDevices = await this.meetingSession.audioVideo.listVideoInputDevices();
        if (videoDevices.length > 0) {
          await this.meetingSession.audioVideo.startVideoInput(videoDevices[0].deviceId);
        }
      }

      // 音声入力（マイク）の開始：プレビューで選択されたマイクがあればそれを使用する
      if (this.selectedAudioId) {
        await this.meetingSession.audioVideo.startAudioInput(this.selectedAudioId);
      } else {
        const audioDevices = await this.meetingSession.audioVideo.listAudioInputDevices();
        if (audioDevices.length > 0) {
          await this.meetingSession.audioVideo.startAudioInput(audioDevices[0].deviceId);
        }
      }

      await this.meetingSession.audioVideo.start();

      // 画面を入室後（会議画面）に切り替える
      this.isJoined = true;

      // VueがDOMを更新(レンダリング)するのを待ってからメディア要素をバインドする
      this.$nextTick(() => {
        this.meetingSession!.audioVideo.bindAudioElement(this.audioElement); // n対nの全員の声をaudio要素から出力させる
        this.meetingSession!.audioVideo.startLocalVideoTile();
      });

      this.isBusy = false;

      return;
    } catch (error) {
      console.log("joinMeeting() chatch the error", error);

      const systemError: SystemError = error as SystemError;

      // ステータスコードが400の場合、エラーメッセージを表示し画面情報を再取得する
      if (systemError.statusCode == 400) {
        this.messageModalTitle = "通話開始できません。";

        // 5分より前に通話開始エラー
        if (systemError.originalResponse.includes("It is too early to start the meeting. Please wait until 5 minutes before the scheduled time.")) {
          // エラーはerrorCollectionsに登録
          await this.sendSystemErrorService.postForPublic(
            { message: `joinMeeting() error. It is too early to start the meeting. Please wait until 5 minutes before the scheduled time. roomId: ${roomId}, type: ${type}` },
            this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
            this.$router
          );

          // モーダルと画面表示でエラーを通知する
          const messages: string[] = [];
          messages.push("開催予定時間5分前まで通話開始できません。");
          messages.push("開催予定時間5分前までお待ちください。");
          this.messageModalMessage = messages.join("\n");
          this.errorMessage = "";
        }
        // 24時間経過エラー
        else if (systemError.originalResponse.includes("Cannot start the meeting. More than 24 hours have passed since the scheduled time.")) {
          // エラーはerrorCollectionsに登録
          await this.sendSystemErrorService.postForPublic(
            { message: `joinMeeting() error. Cannot start the meeting. More than 24 hours have passed since the scheduled time. roomId: ${roomId}, type: ${type}` },
            this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
            this.$router
          );

          // モーダルと画面表示でエラーを通知する
          const messages: string[] = [];
          messages.push("開催予定時間を24時間経過したため、通話開始できません。");
          this.messageModalMessage = messages.join("\n");
          this.errorMessage = messages.join("\n");
        }
        // その他の400エラー
        else {
          // エラーはerrorCollectionsに登録
          await this.sendSystemErrorService.postForPublic(
            { message: `joinMeeting() error. Bad request. roomId: ${roomId}, type: ${type}, response: ${systemError.originalResponse}` },
            this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
            this.$router
          );

          const messages: string[] = [];
          messages.push("通話を開始できませんでした。");
          messages.push("しばらくしてから再度お試しください。");
          this.messageModalMessage = messages.join("\n");
          this.errorMessage = messages.join("\n");
        }

        this.showMessageModal = true;

        // プレビュー再開
        this.startPreview();
      }
      // 404
      else if (systemError.statusCode == 404) {
        // エラーはerrorCollectionsに登録
        await this.sendSystemErrorService.postForPublic(
          { message: `joinMeeting() error. rommId is not found or invalid roomId. roomId: ${roomId}, type: ${type}` },
          this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
          this.$router
        );

        // モーダルと画面表示でエラーを通知する
        this.messageModalTitle = "エラーが発生しました。";
        const messages: string[] = [];
        messages.push("URLに誤りがあるため、通話を開始できません。");
        messages.push("再度URLを確認していただき、もう一度お試しください。");
        this.messageModalMessage = messages.join("\n");
        this.errorMessage = messages.join("\n");

        this.showMessageModal = true;
      } else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /** マイクのミュート切り替え */
  toggleMicMute(): void {
    if (!this.meetingSession) return;
    if (this.isMicMuted) {
      this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
    } else {
      this.meetingSession.audioVideo.realtimeMuteLocalAudio();
    }
    this.isMicMuted = !this.isMicMuted;
  }

  /** スピーカーのミュート切り替え */
  toggleSpeakerMute(): void {
    if (!this.audioElement) return;
    this.isSpeakerMuted = !this.isSpeakerMuted;
    this.audioElement.muted = this.isSpeakerMuted;
  }

  /** ローカルビデオの表示切り替え */
  toggleLocalVideo(): void {
    if (!this.meetingSession) return;

    if (this.isLocalVideoOn) {
      this.meetingSession.audioVideo.stopLocalVideoTile();
    } else {
      this.meetingSession.audioVideo.startLocalVideoTile();
    }
    this.isLocalVideoOn = !this.isLocalVideoOn;
  }

  async onScreenShot(): Promise<void> {
    try {
      this.isBusy = true;
      this.loadingMessage = "アップロード中...";

      // 施設ID、面会IDが有効で無ければエラーモーダルを表示する
      if (!this.facilityId || !this.reservationId) {
        // エラーはerrorCollectionsに登録
        await this.sendSystemErrorService.postForPublic(
          {
            message:
              `onScreenShot() error. facilityId or reservationId is missing.` +
              `roomId: ${this.roomId}, type: ${this.type}, facilityId: ${this.facilityId}, reservationId: ${this.reservationId}`
          },
          this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
          this.$router
        );

        // モーダルと画面表示でエラーを通知する
        this.messageModalTitle = "エラーが発生しました。";
        const messages: string[] = [];
        messages.push("URLに誤りがあるため、通話を開始できません。");
        messages.push("再度URLを確認していただき、もう一度お試しください。");
        this.messageModalMessage = messages.join("\n");
        this.showMessageModal = true;
        return;
      }

      // 映像から画像を作成する
      const imageString: string | null = this.createImageString();
      if (!imageString) {
        return;
      }

      // 画像をアップロード
      await this.uploadImage(imageString);

      // 撮影完了モーダルを表示する
      this.messageModalTitle = "撮影が完了しました";
      this.messageModalMessage = "スクリーンショットを保存しました";
      this.showMessageModal = true;
    } catch (error) {
      // エラーは呼び出し先で処理しているのでここは握り潰すだけ
      console.error("onScreenShot() chatch the error", error);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /** ### 映像部分のスクリーンショットを撮影する */
  private createImageString(): string | null {
    // 撮影対象となる相手の映像ID（常にリモート全員を対象とする）
    const targetTileIds = this.remoteTileIds;

    if (targetTileIds.length === 0) {
      this.messageModalTitle = "エラーが発生しました。";
      this.messageModalMessage = "スクリーンショットの対象となる映像がありません";
      this.showMessageModal = true;
      return null;
    }

    // video要素とtileIdのペアをすべて取得
    const videoDataList: { tileId: number; el: HTMLVideoElement }[] = [];
    for (const tileId of targetTileIds) {
      const el = this.$refs[`remoteVideo-${tileId}`] as HTMLVideoElement | HTMLVideoElement[];
      const videoEl = Array.isArray(el) ? el[0] : el;
      if (videoEl && videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        videoDataList.push({ tileId, el: videoEl });
      }
    }

    if (videoDataList.length === 0) {
      this.messageModalTitle = "エラーが発生しました。";
      this.messageModalMessage = "映像の準備ができていません";
      this.showMessageModal = true;
      return null;
    }

    const isLandscape = this.windowWidth >= this.windowHeight;

    // 1枠あたりのサイズ（最大の映像サイズを基準とする）
    let baseWidth = 0;
    let baseHeight = 0;
    videoDataList.forEach((data) => {
      if (data.el.videoWidth > baseWidth) baseWidth = data.el.videoWidth;
      if (data.el.videoHeight > baseHeight) baseHeight = data.el.videoHeight;
    });
    // 最低限のサイズを保証する
    if (baseWidth === 0) baseWidth = 1280;
    if (baseHeight === 0) baseHeight = 720;

    let canvasWidth = 0;
    let canvasHeight = 0;

    // 各タイルの描画領域情報（x, y, w, h）を格納
    const cellLayouts = new Map<number, { x: number; y: number; w: number; h: number }>();

    if (this.pinnedTileId !== null && videoDataList.length > 1) {
      // ピン留めあり、かつ相手が複数（サムネイルが存在する）場合
      const count = videoDataList.length - 1; // サムネイルの数

      if (isLandscape) {
        // 横長：メインビデオが左(87.5%)、サムネイルが右(12.5%)に縦並び
        canvasWidth = baseWidth / 0.875;
        canvasHeight = baseHeight;

        const mainW = baseWidth;
        const mainH = canvasHeight;
        const thumbW = canvasWidth - baseWidth;
        const thumbH = canvasHeight / count;

        let thumbIndex = 0;
        videoDataList.forEach((data) => {
          if (data.tileId === this.pinnedTileId) {
            cellLayouts.set(data.tileId, { x: 0, y: 0, w: mainW, h: mainH });
          } else {
            cellLayouts.set(data.tileId, { x: mainW, y: thumbIndex * thumbH, w: thumbW, h: thumbH });
            thumbIndex++;
          }
        });
      } else {
        // 縦長：メインビデオが上(87.5%)、サムネイルが下(12.5%)に横並び
        canvasWidth = baseWidth;
        canvasHeight = baseHeight / 0.875;

        const mainW = canvasWidth;
        const mainH = baseHeight;
        const thumbW = canvasWidth / count;
        const thumbH = canvasHeight - baseHeight;

        let thumbIndex = 0;
        videoDataList.forEach((data) => {
          if (data.tileId === this.pinnedTileId) {
            cellLayouts.set(data.tileId, { x: 0, y: 0, w: mainW, h: mainH });
          } else {
            cellLayouts.set(data.tileId, { x: thumbIndex * thumbW, y: mainH, w: thumbW, h: thumbH });
            thumbIndex++;
          }
        });
      }
    } else {
      // ピン留めなし、または相手が1人だけの場合はグリッド表示
      const count = videoDataList.length;
      const rows = isLandscape ? Math.ceil(count / Math.ceil(Math.sqrt(count))) : Math.ceil(Math.sqrt(count));
      const columns = isLandscape ? Math.ceil(Math.sqrt(count)) : Math.ceil(count / rows);
      const emptyCount = rows * columns - count;

      canvasWidth = baseWidth * columns;
      canvasHeight = baseHeight * rows;

      videoDataList.forEach((data, index) => {
        const adjustedIndex = index + emptyCount;
        const row = Math.floor(adjustedIndex / columns);
        const col = adjustedIndex % columns;

        cellLayouts.set(data.tileId, {
          x: col * baseWidth,
          y: row * baseHeight,
          w: baseWidth,
          h: baseHeight
        });
      });
    }

    // キャンバスを作成し、計算したレイアウト通りに全員分の映像を描画する
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // 背景を黒で塗りつぶす（CSSのバックグラウンドと同等）
      ctx.fillStyle = "#000000";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      videoDataList.forEach((data) => {
        const layout = cellLayouts.get(data.tileId);
        if (!layout) return;

        const videoEl = data.el;
        const cellX = layout.x;
        const cellY = layout.y;
        const cellW = layout.w;
        const cellH = layout.h;

        // 映像のアスペクト比を保ちつつ、セルに収める（CSSの object-fit: contain に相当）
        const videoRatio = videoEl.videoWidth / videoEl.videoHeight;
        const cellRatio = cellW / cellH;

        let drawWidth = cellW;
        let drawHeight = cellH;
        let drawX = cellX;
        let drawY = cellY;

        if (videoRatio > cellRatio) {
          drawHeight = cellW / videoRatio;
          drawY = cellY + (cellH - drawHeight) / 2;
        } else {
          drawWidth = cellH * videoRatio;
          drawX = cellX + (cellW - drawWidth) / 2;
        }

        ctx.drawImage(videoEl, drawX, drawY, drawWidth, drawHeight);
      });

      // ワイプ（自分の映像）がオンの場合はキャンバスの左上に重ねて描画する
      if (this.isLocalVideoOn && this.localVideoElement && this.localVideoElement.videoWidth > 0) {
        const wipeWidth = canvas.width * 0.2; // 全体幅の20%
        const wipeHeight = wipeWidth * (this.localVideoElement.videoHeight / this.localVideoElement.videoWidth);
        const marginX = canvas.width * 0.02; // 左端からの余白
        const marginY = canvas.height * 0.02; // 上端からの余白

        ctx.save();
        // 映像の反転描画（CSSの transform: scaleX(-1); をCanvasで再現）
        ctx.translate(marginX + wipeWidth, marginY);
        ctx.scale(-1, 1);
        ctx.drawImage(this.localVideoElement, 0, 0, wipeWidth, wipeHeight);
        ctx.restore();

        // ワイプの枠線を描画する
        ctx.lineWidth = Math.max(2, Math.floor(canvas.width * 0.005));
        ctx.strokeStyle = "#555555";
        ctx.strokeRect(marginX, marginY, wipeWidth, wipeHeight);
      }

      // dataURL（base64）を取得
      const dataUrl = canvas.toDataURL("image/jpeg");
      console.log("スクリーンショットのdataURLを取得しました", dataUrl);
      return dataUrl;
    }

    return null;
  }

  private async uploadImage(imageString: string): Promise<void> {
    let requestBody: UploadImagePostModel | null = null;

    try {
      requestBody = this.createUploadImagePostModel(imageString);
      const meetingSessionType = this.convertMeetingSessionType(this.type as string);
      await this.meetingService.uploadImage(this.roomId as string, meetingSessionType as MeetingSessionType, requestBody as UploadImagePostModel);
      return;
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // エラーはerrorCollectionsに登録
      await this.sendSystemErrorService.postForPublic(
        {
          message:
            `uploadImage() error. api throw error.` +
            `roomId: ${this.roomId}, type: ${this.type}, facilityId: ${this.facilityId}, reservationId: ${this.reservationId}, requestBody: ${JSON.stringify(requestBody)}` +
            `statusCode: ${systemError.statusCode}, originalResponse: ${systemError.originalResponse}`
        },
        this.$route.query.facility_id ? Number(this.$route.query.facility_id) : null,
        this.$router
      );

      // ステータスコードが400、404の場合、エラーメッセージを表示する
      if (systemError.statusCode == 400 || systemError.statusCode == 404) {
        this.messageModalTitle = "エラーが発生しました。";
        this.messageModalMessage = "スクリーンショットを保存できませんでした";
        this.errorMessage = "";
        this.showMessageModal = true;
      } else {
        throw error;
      }
    }

    return;
  }

  private createUploadImagePostModel(imageString: string): UploadImagePostModel {
    return {
      facilityId: this.facilityId as number,
      reservationId: this.reservationId as number,
      image: {
        fileName: this.generateFileName(),
        imageBase64String: imageString
      }
    };
  }

  private generateFileName(): string {
    const now = new Date();
    const pad2 = (n: number) => String(n).padStart(2, "0");
    const pad3 = (n: number) => String(n).padStart(3, "0");

    const date = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
    const time = `${pad2(now.getHours())}-${pad2(now.getMinutes())}-${pad2(now.getSeconds())}-${pad3(now.getMilliseconds())}`;

    return `${date}T${time}-0`;
  }

  /** ビデオのピン留め切り替え */
  togglePin(tileId: number): void {
    if (this.pinnedTileId === tileId) {
      this.pinnedTileId = null;
    } else {
      this.pinnedTileId = tileId;
    }
  }

  /**
   * ### ミーティング種別をAPIリクエスト用の文字列に変換する
   */
  private convertMeetingSessionType(type: string): MeetingSessionType | null {
    switch (type) {
      /** オンライン面会 */
      case "reservation":
        return MeetingSessionType.ONLINE_RESERVATION;
      /** 他事業所連携 */
      case "otheroffice":
        return MeetingSessionType.OTHER_OFFICE;
      /** その他 */
      default:
        return null;
    }
  }

  /**
   * ### ミーティング種別を日本語に変換する
   */
  private convertMeetingSessionTypeName(type: string): string {
    switch (type) {
      /** オンライン面会 */
      case "reservation":
        return "オンライン面会";
      /** 他事業所連携 */
      case "otheroffice":
        return "オンライン会議";
      /** その他 */
      default:
        return "";
    }
  }

  /**
   * ### ウィンドウのリサイズイベントハンドラ
   */
  handleResize(): void {
    this.windowWidth = window.innerWidth;
    this.windowHeight = window.innerHeight;
  }

  /** ビデオグリッドの動的スタイル（参加人数とアスペクト比で計算） */
  get gridStyle(): Record<string, string> {
    // ピン留めされている場合のスタイル
    if (this.pinnedTileId !== null) {
      const isLandscape = this.windowWidth >= this.windowHeight;
      const count = this.remoteTileIds.length - 1; // サムネイルとして表示する人数

      // サムネイルがない場合（相手が1人のみ）は全体表示
      if (count <= 0) {
        return {
          "grid-template-columns": "1fr",
          "grid-template-rows": "1fr"
        };
      }

      if (isLandscape) {
        // 横長：拡大したビデオの右にサムネイルを縦並び（全体の12.5%を確保）
        return {
          "grid-template-columns": "1fr 12.5%",
          "grid-template-rows": `repeat(${count}, minmax(0, 1fr))`
        };
      } else {
        // 縦長：拡大したビデオの下にサムネイルを横並び（全体の12.5%を確保）
        return {
          "grid-template-columns": `repeat(${count}, minmax(0, 1fr))`,
          "grid-template-rows": "1fr 12.5%"
        };
      }
    }

    // 通常時のスタイル
    const count = this.remoteTileIds.length || 1;
    const isLandscape = this.windowWidth >= this.windowHeight;

    // 縦長・横長に応じて平方根を使った最適配置（列・行数）を計算する
    const rows = isLandscape ? Math.ceil(count / Math.ceil(Math.sqrt(count))) : Math.ceil(Math.sqrt(count));
    const columns = isLandscape ? Math.ceil(Math.sqrt(count)) : Math.ceil(count / rows);

    return {
      "grid-template-columns": `repeat(${columns}, minmax(0, 1fr))`,
      "grid-template-rows": `repeat(${rows}, minmax(0, 1fr))`
    };
  }

  /**
   * ### ビデオアイテムごとのスタイルを取得する（ピン留め・サムネイル配置用）
   */
  getItemStyle(tileId: number): Record<string, string> {
    // 通常時は自動配置に任せる
    if (this.pinnedTileId === null) return {};

    const isLandscape = this.windowWidth >= this.windowHeight;

    if (this.pinnedTileId === tileId) {
      // 拡大したビデオをメインエリアに配置
      if (isLandscape) {
        return { "grid-column": "1 / 2", "grid-row": "1 / -1" };
      } else {
        return { "grid-column": "1 / -1", "grid-row": "1 / 2" };
      }
    } else {
      // その他の小さく表示するビデオ（サムネイル）を順番に配置
      const thumbnails = this.remoteTileIds.filter((id) => id !== this.pinnedTileId);
      const index = thumbnails.indexOf(tileId);
      const gridPos = index + 1; // gridの行・列指定は1から始まるため

      if (isLandscape) {
        return { "grid-column": "2 / 3", "grid-row": `${gridPos} / ${gridPos + 1}` };
      } else {
        return { "grid-column": `${gridPos} / ${gridPos + 1}`, "grid-row": "2 / 3" };
      }
    }
  }

  /** 左上に配置する空きセル（余白）の数 */
  get emptyCellCount(): number {
    // ピン留め時はレイアウトが特殊になるためダミー枠は確保しない
    if (this.pinnedTileId !== null) return 0;

    const count = this.remoteTileIds.length || 1;
    const isLandscape = this.windowWidth >= this.windowHeight;

    const rows = isLandscape ? Math.ceil(count / Math.ceil(Math.sqrt(count))) : Math.ceil(Math.sqrt(count));
    const columns = isLandscape ? Math.ceil(Math.sqrt(count)) : Math.ceil(count / rows);

    return rows * columns - count;
  }

  /**
   * 退出処理
   */
  leaveMeeting(): void {
    if (this.meetingSession) {
      if (this.observer) {
        this.meetingSession.audioVideo.removeObserver(this.observer);
        this.observer = null;
      }
      this.meetingSession.audioVideo.stopLocalVideoTile();
      this.meetingSession.audioVideo.unbindAudioElement();
      this.meetingSession.audioVideo.stop();
      this.isJoined = false;
      this.remoteTileIds = [];
      this.isMicMuted = false;
      this.isSpeakerMuted = false;
      this.isLocalVideoOn = true;
      this.pinnedTileId = null;
    }

    // プレビューを再開する
    this.$nextTick(() => {
      this.startPreview();
    });

    return;
  }

  // ライフサイクル
  beforeDestroy(): void {
    window.removeEventListener("resize", this.handleResize);
    this.stopAudioMeter();
    this.leaveMeeting();

    return;
  }
}
</script>

<style scoped lang="scss">
@import "../../styles/pc/_colors.scss";

#meeting-room {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgb(206, 231, 235);
  overflow: hidden;

  .title {
    width: 100%;
    text-align: center;
    padding: 0.5rem;
    font-size: 18px;
    font-weight: bold;
    color: #fff;
    background-color: #0087b4;
    flex-shrink: 0;
  }

  .main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 0;
    flex-basis: 0;
  }

  // --- プレビュー画面 ---
  .preview-container {
    overflow-y: auto;
    width: 100%;
    flex: 1;
    min-height: 0;

    .video-preview {
      width: 100%;
      min-height: 0;
      margin-bottom: 1rem;
      display: flex;
      justify-content: center;
      align-items: center;

      .preview-video {
        width: 100%;
        max-width: 480px;
        height: 100%;
        max-height: 70vh;
        object-fit: contain;
        transform: scaleX(-1);
      }
    }

    .device-selectors {
      width: 100%;
      max-width: 480px;

      .audio-indicator {
        width: 100%;
        margin-top: 10px;

        p {
          margin-bottom: 5px;
        }
        .meter {
          width: 100%;
          height: 10px;
          background-color: #ddd;
          border-radius: 5px;
          overflow: hidden;
          .meter-bar {
            height: 100%;
            background-color: #4caf50;
            transition: width 0.1s ease-out;
          }
        }
      }
    }

    .meeting-start-btn {
      width: 120px;
      min-width: 120px;
    }
  }

  // --- 会議画面 ---
  .meeting-container {
    background-color: #111;
    overflow: hidden;
    flex: 1;
    min-height: 0;
    flex-basis: 0;

    .video-wrapper {
      position: relative;
      background-color: #000;
      flex: 1;
      min-height: 0;
      flex-basis: 0;

      .local-video-wipe {
        position: absolute;
        top: 8px;
        left: 8px;

        /* 縦長画面の場合 */
        @media (orientation: portrait) {
          // 画面の横幅の15%(15vw)から左右5%ずつ（計10%）を削る計算
          width: calc(15vw * (100 - 5 * 2) / 100);
          // 画面の縦幅の18%(18vh)から上下15%ずつ（計30%）を削る計算
          height: calc(18vh * (100 - 15 * 2) / 100);

          video {
            // 映像自体は削る前の基準サイズ（15vw / 18vh）で描画する
            width: 15vw;
            height: 18vh;
            // 左上から削る割合分だけマイナスにずらしてはみ出させる（左5%、上15%）
            left: calc(-15vw * 5 / 100);
            top: calc(-18vh * 15 / 100);
          }
        }
        /* 横長画面の場合 */
        @media (orientation: landscape) {
          width: calc(18vw * (100 - 15 * 2) / 100);
          height: calc(15vh * (100 - 5 * 2) / 100);

          video {
            width: 18vw;
            height: 15vh;
            left: calc(-18vw * 15 / 100);
            top: calc(-15vh * 5 / 100);
          }
        }

        background-color: #000;
        border: 2px solid #555;
        border-radius: 6px;
        z-index: 10;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
        overflow: hidden;

        // ビデオ共通
        video {
          position: absolute;
          display: block;
          object-fit: cover; /* はみ出した分トリミングされるようにする */
        }

        // スマホなど狭い画面では余白を小さくする
        @media screen and (max-width: 768px) {
          top: 4px;
          left: 4px;
        }
      }

      .video-grid {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: grid;
        gap: 2px;
        background-color: #222;

        .video-item {
          width: 100%;
          height: 100%;
          background: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 0;
          min-height: 0;

          &.clickable {
            cursor: pointer;
          }

          video {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }
      }
    }

    .controls {
      background-color: #222;
      flex-shrink: 0;

      button {
        color: #fff;
        border-color: #555;

        &:hover {
          background-color: #444;
        }
      }

      .leave-meeting-btn {
        width: 60px;
        min-width: 60px;
        background-color: #e53935;
        color: #fff;
        border: none;

        &:hover {
          background-color: #d32f2f;
        }
      }
    }
  }

  .version {
    width: 100%;
    text-align: right;
    flex-shrink: 0;

    font-size: 14px;
    color: #fff;
    background-color: #0087b4;
  }
}
</style>
